import React, { useEffect, useState } from 'react';
import { Button, Dropdown, Modal, Menu as AntMenu, Table, Grid } from 'antd';
import {
    SwapOutlined,
    InfoCircleOutlined,
    ArrowLeftOutlined
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../../components/Header/Header';
import Menu from '../../components/Menu/Menu';
import { listService } from '../../services/ListService';
import './Assignments.scss';
import ListStatusGuard from './ListStatusGuard';
import StatusTag from '../../components/StatusTag/StatusTag';

const AssignedProducts = () => {
    const StatusDot = ({ purchased }) => (
        <span
            style={{
                display: 'inline-block',
                width: 10,
                height: 10,
                borderRadius: '50%',
                backgroundColor: purchased ? '#52c41a' : '#ff4d4f',
                marginRight: 8
            }}
        />
    );

    const { id, supplier_id } = useParams();
    const navigate = useNavigate();
    const [isUnassigned, setIsUnassigned] = useState(false);

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [title, setTitle] = useState('');
    const [listStatus, setListStatus] = useState('manual_processing');
    const [supplierListStatus, setSupplierListStatus] = useState('open');
    const [infoModalOpen, setInfoModalOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;
    useEffect(() => {
        fetchProducts();
    }, [id, supplier_id]);

    const fetchProducts = () => {
        setLoading(true);

        listService
            .getManualProcessingSupplierDetails(id, supplier_id)
            .then(res => {
                const isUnassignedList = !res.data.supplier;
                const nextSupplierListStatus = ListStatusGuard.getManualSupplierStatus(
                    res.data?.supplier_list || res.data
                );

                setIsUnassigned(isUnassignedList);
                setListStatus(res.data?.necessity_list?.status || res.data?.status || 'manual_processing');
                setSupplierListStatus(nextSupplierListStatus);
                setTitle(
                    isUnassignedList
                        ? 'Produse neatribuite'
                        : `Produse atribuite – ${res.data.supplier?.name}`
                );

                setData(res.data.items || []);
            })
            .finally(() => setLoading(false));
    };

    const renderActions = (_, record) => {
        const canManageManualProcessingItems = ListStatusGuard.canManageManualProcessingItems(
            listStatus,
            supplierListStatus
        );
        const canMoveToUnassigned = ListStatusGuard.canMoveManualProcessingItemToUnassigned(
            listStatus,
            supplierListStatus,
            isUnassigned
        );

        const menu = (
            <AntMenu
                items={[
                    {
                        key: 'purchased',
                        label: (
                            <span>
                                <StatusDot purchased />
                                Achiziționat
                            </span>
                        ),
                        disabled: !canManageManualProcessingItems || record.procurement_status === 'purchased',
                        onClick: () =>
                            listService
                                .updateManualProcessingItemStatus(record.id, 'purchased')
                                .then(fetchProducts)
                    },
                    {
                        key: 'not_purchased',
                        label: (
                            <span>
                                <StatusDot purchased={false} />
                                Neachiziționat
                            </span>
                        ),
                        disabled: !canManageManualProcessingItems || record.procurement_status === 'not_purchased',
                        onClick: () =>
                            listService
                                .updateManualProcessingItemStatus(record.id, 'not_purchased')
                                .then(fetchProducts)
                    },
                    { type: 'divider' },

                    ...(!isUnassigned ? [{
                        key: 'unassign',
                        label: 'Mută în neatribuite',
                        icon: <SwapOutlined />,
                        disabled: !canMoveToUnassigned,
                        onClick: () =>
                            listService
                                .moveItemToUnassigned(record.id)
                                .then(fetchProducts)
                    }] : []),

                    {
                        key: 'info',
                        label: 'Vezi informații',
                        icon: <InfoCircleOutlined />,
                        disabled: !record.supplementary_info,
                        onClick: () => {
                            setSelectedItem(record);
                            setInfoModalOpen(true);
                        }
                    }
                ]}
            />
        );

        return (
            <Dropdown overlay={menu} trigger={['click']} placement="bottomRight">
                <Button>⋮</Button>
            </Dropdown>
        );
    };

    const columns = [
        {
            title: 'Nume',
            key: 'name',
            render: (_, record) => (
                <>
                    <StatusDot purchased={record.procurement_status === 'purchased'} />
                    {record.product?.name}
                </>
            )
        },
        {
            title: 'Cantitate',
            key: 'quantity',
            render: (_, record) =>
                `${record.quantity} ${record.unit_measurement || ''}`
        },
        {
            title: 'Status',
            key: 'status',
            render: (_, record) => <StatusTag status={record.procurement_status} />
        },
        {
            title: 'Opțiuni',
            key: 'actions',
            width: 140,
            render: renderActions
        }
    ];

    const MobileList = () => {
        if (data.length === 0) {
            return (
                <div className="list__empty-state">
                    <p className="list__empty-state-text">Nu există produse în această listă.</p>
                </div>
            );
        }

        return (
            <div className="list__mobile-table-wrapper">
                <div className="list__mobile-table-header">
                    <div className="list__mobile-table-header-cell list__mobile-table-header-name">Nume</div>
                    <div className="list__mobile-table-header-cell" style={{ width: '100px', flexShrink: 0 }}>Cantitate</div>
                    <div className="list__mobile-table-header-cell" style={{ width: '130px', flexShrink: 0 }}>Status</div>
                    <div className="list__mobile-table-header-cell list__mobile-table-header-actions" style={{ width: '60px', flexShrink: 0 }}>Opt</div>
                </div>
                <div className="list__mobile-list">
                    {data.map((row) => (
                        <div key={row.id} className="list__mobile-card">
                            <div className="list__mobile-card-cell list__mobile-card-name">
                                <StatusDot purchased={row.procurement_status === 'purchased'} />
                                {row.product?.name}
                            </div>
                            <div className="list__mobile-card-cell" style={{ width: '100px', flexShrink: 0 }}>
                                {row.quantity} {row.unit_measurement || ''}
                            </div>
                            <div className="list__mobile-card-cell" style={{ width: '130px', flexShrink: 0 }}>
                                <StatusTag status={row.procurement_status} />
                            </div>
                            <div className="list__mobile-card-cell list__mobile-card-actions" style={{ width: '60px', flexShrink: 0 }}>
                                {renderActions(null, row)}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <div className="standard products list assignments-page">
            <Header />

            <div className="standard__container list assignments-page__container">
                <Menu />
                <div className="standard__wrapper assignments-page__wrapper">
                    <div style={{ padding: 24 }}>

                        <h2 className="standard__page-title">
                            {title}
                        </h2>

                        {!isMobile ? (
                            <Table
                                dataSource={data}
                                columns={columns}
                                rowKey="id"
                                loading={loading}
                                pagination={false}
                                scroll={{ x: true }}
                            />
                        ) : (
                            <MobileList />
                        )}
                        <div className='list__back'>
                            <Button
                                type="link"
                                icon={<ArrowLeftOutlined />}
                                onClick={() => navigate(-1)}
                                style={{ padding: 0, color: 'initial' }}
                            >
                                Înapoi
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
            <Modal
                title="Informații produs"
                open={infoModalOpen}
                onCancel={() => setInfoModalOpen(false)}
                footer={null}
            >
                <p><strong>Produs:</strong> {selectedItem?.product?.name}</p>
                <p><strong>Informații suplimentare:</strong></p>
                <p>{selectedItem?.supplementary_info}</p>
            </Modal>
        </div>
    );
};

export default AssignedProducts;